import {
  Component as ReactComponent,
  lazy,
  Suspense,
  useDeferredValue,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ComponentType,
  type ReactNode,
} from "react"

// Optional artwork must not replace the page when its download or render fails.
// Its parent retains the reserved space; the hidden marker exposes the settled
// failure without adding an error message or controls to a decorative scene.
class IllustrationErrorBoundary extends ReactComponent<
  { children: ReactNode },
  { failed: boolean }
> {
  override state = { failed: false }

  static getDerivedStateFromError() {
    return { failed: true }
  }

  override render() {
    return this.state.failed ? <span hidden data-illustration-unavailable /> : this.props.children
  }
}

/**
 * Render the real illustration on the server, but leave its Suspense boundary
 * dehydrated in the browser until near the viewport. Unlike conditionally
 * mounting a lazy component, this retains the server's markup and dimensions.
 *
 * Only use for decorative scenes. Navigation and actionable controls must stay
 * outside: React does not replay every discrete event across a pending boundary.
 */
export function deferUntilVisible<Props extends object>(
  load: () => Promise<{ default: ComponentType<Props> }>,
) {
  return function DeferredIllustration(props: Props) {
    const ref = useRef<HTMLDivElement>(null)
    const [gate] = useState(() => {
      let activate = () => {}
      const visible = new Promise<void>((resolve) => {
        activate = resolve
      })
      const Component = lazy(async () => {
        if (!import.meta.env.SSR) await visible
        return load()
      })
      return { Component, activate }
    })

    useEffect(() => {
      // display:contents preserves the illustration's existing layout. Observe
      // its real layout container, not the boxless wrapper.
      const target = ref.current?.parentElement
      if (!target) return
      if (typeof IntersectionObserver === "undefined") {
        gate.activate()
        return
      }
      const observer = new IntersectionObserver(
        (entries) => {
          if (entries.some((entry) => entry.isIntersecting)) {
            gate.activate()
            observer.disconnect()
          }
        },
        { rootMargin: "400px 0px" },
      )
      observer.observe(target)
      // React queues events targeting a dehydrated boundary, so a synthetic
      // onFocus handler cannot release that same boundary. Capture native
      // intent above the React root to start loading without waiting for IO.
      const document = target.ownerDocument
      const activateOnIntent = (event: Event) => {
        if (event.target instanceof Node && ref.current?.contains(event.target)) gate.activate()
      }
      const events = ["focusin", "pointerdown", "click"] as const
      for (const event of events) document.addEventListener(event, activateOnIntent, true)
      return () => {
        observer.disconnect()
        for (const event of events) document.removeEventListener(event, activateOnIntent, true)
      }
    }, [gate])

    const { Component } = gate
    // Keep the old boundary identity during urgent parent updates. New props
    // can commit once the scene is ready, without replacing server art with null.
    const deferredProps = useDeferredValue(props)
    const scene = useMemo(
      () => (
        <IllustrationErrorBoundary>
          <Suspense fallback={null}>
            <Component {...deferredProps} />
          </Suspense>
        </IllustrationErrorBoundary>
      ),
      [Component, deferredProps],
    )
    return (
      <div ref={ref} className="contents" data-deferred-illustration>
        {scene}
      </div>
    )
  }
}
